import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Badge } from '@/components/ui/badge';
import CityAutocomplete from '@/components/onboarding/CityAutocomplete';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import AvatarCropDialog from '@/components/AvatarCropDialog';
import { useProfile, useUpdateProfile } from '@/hooks/useProfile';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Edit2, Copy, Check, Building2, MapPin, Heart, ExternalLink, Plus, Camera } from 'lucide-react';

export default function ProfilePage() {
  const { user } = useAuth();
  const { data: profile, isLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const { toast } = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [inviteCodes, setInviteCodes] = useState<any[]>([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const avatarInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Please upload a JPEG, PNG, GIF, or WebP image.', variant: 'destructive' });
      if (avatarInputRef.current) avatarInputRef.current.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum size is 10MB.', variant: 'destructive' });
      if (avatarInputRef.current) avatarInputRef.current.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setCropImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleCroppedUpload = async (blob: Blob) => {
    if (!user?.id) return;
    setCropImageSrc(null);
    setUploadingAvatar(true);
    try {
      const fileName = `avatar-${Date.now()}.jpg`;
      const storagePath = `avatars/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('post-media')
        .upload(storagePath, blob, { contentType: 'image/jpeg', cacheControl: '31536000', upsert: true });

      if (uploadError) throw uploadError;

      await updateProfile.mutateAsync({ avatar_url: storagePath });
      toast({ title: 'Profile photo updated!' });
    } catch (error: any) {
      toast({ title: 'Upload failed', description: error.message, variant: 'destructive' });
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const [formData, setFormData] = useState({
    full_name: '',
    pronouns: '',
    headline: '',
    company: '',
    role: '',
    linkedin_url: '',
    city_cohort: '',
    is_mentor: false,
    bio: '',
  });

  const normalizeLinkedInUrl = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
      const parsed = new URL(withProtocol);
      const host = parsed.hostname.toLowerCase();
      if (host === 'linkedin.com' || host.endsWith('.linkedin.com')) {
        return parsed.toString();
      }
      return null;
    } catch {
      return null;
    }
  };

  React.useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        pronouns: profile.pronouns || '',
        headline: profile.headline || '',
        company: profile.company || '',
        role: profile.role || '',
        linkedin_url: profile.linkedin_url || '',
        city_cohort: profile.city_cohort || '',
        is_mentor: profile.is_mentor || false,
        bio: profile.bio || '',
      });
    }
  }, [profile]);

  const loadInviteCodes = async () => {
    if (!user?.id) return;
    setLoadingCodes(true);
    try {
      const { data, error } = await supabase
        .from('invite_codes')
        .select('*')
        .eq('created_by_user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInviteCodes(data || []);
    } catch (error) {
      console.error('Error loading invite codes:', error);
    } finally {
      setLoadingCodes(false);
    }
  };

  const generateInviteCode = async () => {
    if (!user?.id) return;
    try {
      const code = `QBN${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      const { error } = await supabase
        .from('invite_codes')
        .insert({ code, created_by_user_id: user.id });

      if (error) throw error;
      
      await loadInviteCodes();
      toast({ title: 'Invite code generated!', description: code });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleSave = async () => {
    try {
      const normalizedLinkedIn = normalizeLinkedInUrl(formData.linkedin_url);
      if (formData.linkedin_url && !normalizedLinkedIn) {
        toast({
          title: 'Invalid LinkedIn URL',
          description: 'Please enter a valid linkedin.com profile URL.',
          variant: 'destructive',
        });
        return;
      }

      await updateProfile.mutateAsync({
        ...formData,
        linkedin_url: normalizedLinkedIn || '',
      });
      setIsEditing(false);
      toast({ title: 'Profile updated!' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Profile</h1>
          <p className="text-muted-foreground text-sm">
            Manage your QBN presence
          </p>
        </div>
        <Button
          variant={isEditing ? 'outline' : 'default'}
          onClick={() => setIsEditing(!isEditing)}
          className={!isEditing ? 'btn-qbn-primary' : ''}
        >
          <Edit2 className="h-4 w-4 mr-2" />
          {isEditing ? 'Cancel' : 'Edit Profile'}
        </Button>
      </div>

      {/* Profile Card */}
      <Card className="card-qbn overflow-hidden">
        {/* Banner */}
        <div className="h-24 bg-gradient-primary" />
        
        <CardContent className="relative pt-0 pb-6">
          {/* Avatar with upload */}
          <div className="relative w-24 -mt-12 mb-4">
            <Avatar className="h-24 w-24 border-4 border-card">
              <SignedAvatarImage src={profile?.avatar_url || undefined} />
              <AvatarFallback className="bg-gradient-primary text-white text-2xl font-display">
                {profile?.full_name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="absolute inset-0 rounded-full flex items-center justify-center bg-black/0 hover:bg-black/40 transition-colors group cursor-pointer"
            >
              {uploadingAvatar ? (
                <Loader2 className="h-6 w-6 animate-spin text-white" />
              ) : (
                <Camera className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>

          <AvatarCropDialog
            open={!!cropImageSrc}
            imageSrc={cropImageSrc || ''}
            onCrop={handleCroppedUpload}
            onCancel={() => {
              setCropImageSrc(null);
              if (avatarInputRef.current) avatarInputRef.current.value = '';
            }}
          />

          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pronouns">Pronouns</Label>
                  <Input
                    id="pronouns"
                    value={formData.pronouns}
                    onChange={(e) => setFormData(prev => ({ ...prev, pronouns: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="headline">Headline</Label>
                <Input
                  id="headline"
                  value={formData.headline}
                  onChange={(e) => setFormData(prev => ({ ...prev, headline: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Input
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkedin_url">LinkedIn URL</Label>
                <Input
                  id="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="city_cohort">Location</Label>
                <CityAutocomplete
                  value={formData.city_cohort}
                  onChange={(city) => setFormData(prev => ({ ...prev, city_cohort: city }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  rows={4}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <Label htmlFor="is_mentor" className="font-medium">Open to Matchups</Label>
                  <p className="text-sm text-muted-foreground">Get personalized matchups based on your skillsets and interests</p>
                </div>
                <Switch
                  id="is_mentor"
                  checked={formData.is_mentor}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_mentor: checked }))}
                />
              </div>

              <Button
                onClick={handleSave}
                disabled={updateProfile.isPending}
                className="w-full btn-qbn-primary"
              >
                {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-foreground">{profile?.full_name}</h2>
                  {profile?.is_mentor && (
                    <Badge className="badge-mentor">
                      <Heart className="h-3 w-3 mr-1" />
                      Matchups
                    </Badge>
                  )}
                </div>
                {profile?.pronouns && (
                  <span className="text-sm text-muted-foreground">({profile.pronouns})</span>
                )}
              </div>

              {profile?.headline && (
                <p className="text-muted-foreground">{profile.headline}</p>
              )}

              <div className="flex flex-wrap gap-4 text-sm">
                {profile?.company && (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                    {profile.company}
                  </span>
                )}
                {profile?.city_cohort && (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    {profile.city_cohort} Cohort
                  </span>
                )}
              </div>

              {profile?.bio && (
                <div className="space-y-2 text-foreground whitespace-pre-wrap">
                  {profile.bio}
                </div>
              )}

              {profile?.linkedin_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View LinkedIn
                  </a>
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite Codes Section */}
      <Card className="card-qbn">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Your Invite Codes</CardTitle>
            <Dialog onOpenChange={(open) => open && loadInviteCodes()}>
              <DialogTrigger asChild>
                <Button size="sm" className="btn-qbn-primary">
                  <Plus className="h-4 w-4 mr-1" />
                  Generate Code
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Manage Invite Codes</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Share these codes with trusted professionals to invite them to QBN.
                    Each code can only be used once.
                  </p>
                  
                  <Button onClick={generateInviteCode} className="w-full btn-qbn-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    Generate New Code
                  </Button>

                  {loadingCodes ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : inviteCodes.length > 0 ? (
                    <div className="space-y-2">
                      {inviteCodes.map((code) => (
                        <div
                          key={code.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            code.is_used ? 'bg-muted/50' : 'bg-muted'
                          }`}
                        >
                          <div>
                            <code className={`font-mono ${code.is_used ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                              {code.code}
                            </code>
                            {code.is_used && (
                              <p className="text-xs text-muted-foreground mt-1">Used</p>
                            )}
                          </div>
                          {!code.is_used && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyCode(code.code)}
                            >
                              {copiedCode === code.code ? (
                                <Check className="h-4 w-4 text-emerald-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">
                      No invite codes yet. Generate one to invite someone!
                    </p>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Invite trusted professionals to join QBN. Your reputation is linked to the members you invite.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
